import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Flex,
  Grid,
  Heading,
  Text,
  Badge,
  Progress,
  Alert,
  AlertIcon,
  Spinner,
  VStack,
  HStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { Evento } from "../types/Event";
import { GiftCard } from "../types/Booking";
import { eventService } from "../services/eventService";
import { bookingService } from "../services/bookingService";
import BookingModal from "../components/BookingModal";
import GiftCardModal from "../components/GiftCardModal";
import { getUserInfo, ClientPrincipal, AUTHORIZED_EMAIL } from "../authConfig";

// Helper function to create URL-friendly slug from title
const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/['''.,]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const Home: React.FC = () => {
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const navigate = useNavigate();
  const { eventoSlug, giftcard } = useParams<{ eventoSlug?: string; giftcard?: string }>();
  const [eventi, setEventi] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvento, setSelectedEvento] = useState<Evento | null>(null);
  const [isWaitingList, setIsWaitingList] = useState(false);
  const [menuEvento, setMenuEvento] = useState<Evento | null>(null);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [loadingGiftCards, setLoadingGiftCards] = useState(false);
  const [newsletterEventoId, setNewsletterEventoId] = useState<number | null>(null);
  const [newsletterHtml, setNewsletterHtml] = useState<string | null>(null);
  const [loadingNewsletterPreview, setLoadingNewsletterPreview] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const toast = useToast();

  const isAuthenticated = !!user;

  useEffect(() => {
    const fetchUser = async () => {
      const userInfo = await getUserInfo();
      // Only set admin user if authorized email
      if (userInfo && userInfo.userDetails === AUTHORIZED_EMAIL) {
        setUser(userInfo);
      }
    };
    fetchUser();
    loadEventi();
  }, []);

  // Load gift cards when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadGiftCards();
    }
  }, [isAuthenticated]);

  // Auto-open modal when eventoSlug is in URL
  useEffect(() => {
    if (eventoSlug && eventi && eventi.length > 0) {
      const candidati = eventi.filter(e => createSlug(e.Titolo) === eventoSlug);
      const evento = candidati.find(e => e.Attivo !== false);
      if (evento) {
        const disponibili = evento.PostiTotali - evento.PostiOccupati;
        setSelectedEvento(evento);
        setIsWaitingList(disponibili <= 0);
      } else if (candidati.length > 0) {
        toast({
          title: "Evento non disponibile",
          description: "Questa serata non è più attiva.",
          status: "error",
          duration: 5000,
        });
        navigate("/serate", { replace: true });
      }
    }
  }, [eventoSlug, eventi, navigate, toast]);

  // Auto-open gift card modal when giftcard param is in URL
  useEffect(() => {
    if (giftcard === 'acquista') {
      setShowGiftCardModal(true);
    }
  }, [giftcard]);

  const loadEventi = async () => {
    try {
      setLoading(true);
      const data = await eventService.getEventi();
      setEventi(data);
      setError(null);
    } catch (err) {
      setError("Errore nel caricamento degli eventi");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadGiftCards = async () => {
    try {
      setLoadingGiftCards(true);
      const data = await bookingService.getGiftCards();
      setGiftCards(data);
    } catch (err) {
      console.error("Errore nel caricamento delle gift card:", err);
    } finally {
      setLoadingGiftCards(false);
    }
  };

  const handleDeleteGiftCard = async (giftCardId: number) => {
    if (window.confirm("Sei sicuro di voler eliminare questa gift card?")) {
      try {
        await bookingService.deleteGiftCard(giftCardId);
        setGiftCards(prev => prev.filter(gc => gc.GiftCardId !== giftCardId));
        toast({
          title: "Gift card eliminata",
          status: "success",
          duration: 3000,
        });
      } catch (err: any) {
        toast({
          title: "Errore",
          description: err.message || "Errore nell'eliminazione della gift card",
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const handleApproveGiftCard = async (giftCardId: number) => {
    try {
      await bookingService.approveGiftCard(giftCardId);
      setGiftCards(prev => prev.map(gc => 
        gc.GiftCardId === giftCardId ? { ...gc, Approvata: true } : gc
      ));
      toast({
        title: "Gift card approvata",
        description: "L'email con il codice è stata inviata al cliente",
        status: "success",
        duration: 3000,
      });
    } catch (err: any) {
      toast({
        title: "Errore",
        description: err.message || "Errore nell'approvazione della gift card",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleGiftCardSuccess = () => {
    setShowGiftCardModal(false);
    loadGiftCards();
    toast({
      title: "Richiesta gift card ricevuta",
      description: "È in attesa di approvazione.",
      status: "info",
      duration: 5000,
      isClosable: true,
    });
  };

  // Filter events: show all for admins, only active for public
  const filteredEventi = isAuthenticated 
    ? eventi 
    : eventi.filter(e => e.Attivo !== false);

  const handleDuplicate = async (id: number) => {
    try {
      const nuovoEvento = await eventService.duplicateEvento(id);
      setEventi(prev => [...prev, nuovoEvento]);
      toast({
        title: "Evento duplicato",
        description: "Modifica la data e riattiva l'evento quando è pronto.",
        status: "success",
        duration: 4000,
      });
      navigate(`/eventi/${nuovoEvento.EventoId}`);
    } catch (err) {
      toast({
        title: "Errore",
        description: "Errore nella duplicazione dell'evento",
        status: "error",
        duration: 3000,
      });
      console.error(err);
    }
  };

  const handleOpenNewsletterPreview = async (id: number) => {
    setNewsletterEventoId(id);
    setNewsletterHtml(null);
    setLoadingNewsletterPreview(true);
    try {
      const { html } = await eventService.getEventNewsletterPreview(id);
      setNewsletterHtml(html);
    } catch (err) {
      toast({
        title: "Errore",
        description: "Errore nel caricamento dell'anteprima della newsletter",
        status: "error",
        duration: 3000,
      });
      console.error(err);
      setNewsletterEventoId(null);
    } finally {
      setLoadingNewsletterPreview(false);
    }
  };

  const handleCloseNewsletterPreview = () => {
    setNewsletterEventoId(null);
    setNewsletterHtml(null);
  };

  const handleConfirmSendNewsletter = async () => {
    if (!newsletterEventoId) return;
    setSendingNewsletter(true);
    try {
      await eventService.sendEventNewsletter(newsletterEventoId);
      toast({
        title: "Newsletter inviata",
        description: "Email inviata a scarpetta.homerestaurant@gmail.com",
        status: "success",
        duration: 4000,
      });
      handleCloseNewsletterPreview();
    } catch (err) {
      toast({
        title: "Errore",
        description: err instanceof Error ? err.message : "Errore nell'invio della newsletter",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      console.error(err);
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Sei sicuro di voler eliminare questo evento?")) {
      try {
        await eventService.deleteEvento(id);
        setEventi(prev => prev.filter((e) => e.EventoId !== id));
        toast({
          title: "Evento eliminato",
          status: "success",
          duration: 3000,
        });
      } catch (err) {
        setError("Errore nell'eliminazione dell'evento");
        console.error(err);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMonthYear = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      month: "long",
      year: "numeric",
    });
  };

  // Public visitors only see month/year for events flagged with MostraGiornoEsatto = false;
  // admins always see the exact date since they need it to manage the event.
  const getDisplayDate = (evento: Evento) => {
    if (!isAuthenticated && evento.MostraGiornoEsatto === false) {
      return formatMonthYear(evento.DataEvento);
    }
    return formatDate(evento.DataEvento);
  };

  const getPostiDisponibili = (evento: Evento) => {
    return evento.PostiTotali - evento.PostiOccupati;
  };

  const handleCloseModal = () => {
    setSelectedEvento(null);
    navigate("/", { replace: true });
  };

  const handleBookingSuccess = () => {
    setSelectedEvento(null);
    toast({
      title: isWaitingList ? "Iscrizione alla lista d'attesa completata!" : "Prenotazione confermata!",
      description: "Riceverai una email di conferma.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    loadEventi();
    navigate("/", { replace: true });
  };

  return (
    <Box>
      {/* Back Button */}
      <Button
        as={RouterLink}
        to="/"
        variant="ghost"
        size="sm"
        mb={4}
        color="gray.400"
        _hover={{ color: "white" }}
      >
        ← Indietro
      </Button>

      {error && (
        <Alert status="error" borderRadius="md" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Events Section */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">
          Eventi
        </Heading>
        {isAuthenticated && (
          <HStack spacing={3}>
            <Button
              variant="outline"
              size="md"
              onClick={async () => {
                try {
                  const response = await fetch('/api/newsletter/emails');
                  if (!response.ok) throw new Error('Errore API');
                  const emails: string[] = await response.json();
                  if (emails.length === 0) {
                    toast({ title: "Nessun iscritto", status: "info", duration: 3000 });
                    return;
                  }
                  await navigator.clipboard.writeText(emails.join(', '));
                  toast({ title: `${emails.length} email copiate negli appunti`, status: "success", duration: 3000 });
                } catch (err) {
                  toast({ title: "Errore nel recupero delle email", status: "error", duration: 3000 });
                }
              }}
            >
              📧 Iscritti newsletter
            </Button>
            <Button as={RouterLink} to="/eventi/nuovo" variant="primary" size="md">
              + Nuovo Evento
            </Button>
          </HStack>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={12}>
          <Spinner size="xl" color="gray.400" />
        </Flex>
      ) : filteredEventi.length === 0 ? (
        <Card bg="gray.850" borderColor="gray.700">
          <CardBody textAlign="center" py={12}>
            <Text color="gray.400" mb={4}>Nessun evento in programma al momento.</Text>
            {isAuthenticated && (
              <Button as={RouterLink} to="/eventi/nuovo" variant="primary">
                Crea il primo evento
              </Button>
            )}
          </CardBody>
        </Card>
      ) : (
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
          {[...filteredEventi]
            .sort((a, b) => {
              // Sort: active+bookable first, active+full second, inactive third
              const getScore = (e: Evento) => {
                if (e.Attivo === false) return 2; // inactive
                const disponibili = e.PostiTotali - e.PostiOccupati;
                if (disponibili <= 0) return 1; // active but full
                return 0; // active and bookable
              };
              const scoreA = getScore(a);
              const scoreB = getScore(b);
              if (scoreA !== scoreB) return scoreA - scoreB;
              // Same status: sort by date descending (later events first)
              return new Date(b.DataEvento).getTime() - new Date(a.DataEvento).getTime();
            })
            .map((evento) => {
              const statusConfig = evento.Attivo === false
                ? { label: "Non attivo", color: "gray.600", textColor: "gray.400" }
                : evento.Prenotabile === false
                ? { label: "Prenotabile prossimamente", color: "yellow.600", textColor: "yellow.100" }
                : { label: "Prenotabile", color: "green.600", textColor: "green.200" };
              
              return (
            <Card
              key={evento.EventoId}
              bg="gray.850"
              borderColor="gray.700"
              borderWidth="1px"
              opacity={evento.Attivo === false ? 0.6 : 1}
              _hover={{ borderColor: "gray.500", transform: "translateY(-2px)" }}
              transition="all 0.2s"
            >
              <CardHeader pb={2}>
                <Flex justify="space-between" align="start">
                  <Box flex={1}>
                    <Heading size="md" color="white" mb={1}>
                      {evento.Titolo}
                    </Heading>
                    <Badge bg={statusConfig.color} color={statusConfig.textColor} fontSize="xs" mb={2}>
                      {statusConfig.label}
                    </Badge>
                  </Box>
                  {evento.Menu && (
                    <Tooltip label="Visualizza Menu" placement="top">
                      <IconButton
                        aria-label="Visualizza Menu"
                        icon={<Text fontSize="sm">📜</Text>}
                        size="sm"
                        variant="ghost"
                        onClick={() => setMenuEvento(evento)}
                      />
                    </Tooltip>
                  )}
                </Flex>
                <Text fontSize="sm" color="gray.400" textTransform="capitalize">
                  📅 {getDisplayDate(evento)}
                  {isAuthenticated && evento.MostraGiornoEsatto === false && (
                    <Text as="span" color="yellow.400" fontSize="xs" ml={2} textTransform="none">
                      (data nascosta al pubblico)
                    </Text>
                  )}
                </Text>
              </CardHeader>

              <CardBody py={4}>
                <HStack spacing={4} mb={3}>
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Totali</Text>
                    <Text fontWeight="bold">{evento.PostiTotali}</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Occupati</Text>
                    <Text fontWeight="bold">{evento.PostiOccupati}</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Disponibili</Text>
                    <Text
                      fontWeight="bold"
                      color={getPostiDisponibili(evento) > 0 ? "white" : "gray.500"}
                    >
                      {getPostiDisponibili(evento)}
                    </Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontSize="xs" color="gray.500">Lista Attesa</Text>
                    <Text
                      fontWeight="bold"
                      color={(evento.ListaAttesaCount ?? 0) > 0 ? "yellow.400" : "gray.500"}
                    >
                      {evento.ListaAttesaCount ?? 0}
                    </Text>
                  </VStack>
                </HStack>

                <Progress
                  value={(evento.PostiOccupati / evento.PostiTotali) * 100}
                  size="sm"
                  colorScheme="gray"
                  borderRadius="full"
                  bg="gray.700"
                />
              </CardBody>

              {isAuthenticated && (
                <CardFooter pt={0} borderTop="1px" borderColor="gray.700">
                  <VStack w="full" spacing={3}>
                    <HStack w="full" spacing={2}>
                      <Button
                        flex={1}
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/eventi/${evento.EventoId}/prenotazioni`)}
                      >
                        📋 Prenotazioni
                      </Button>
                      <Button
                        flex={1}
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/eventi/${evento.EventoId}`)}
                      >
                        ✏️ Modifica
                      </Button>
                      <Button
                        flex={1}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDuplicate(evento.EventoId!)}
                      >
                        ⧉ Duplica
                      </Button>
                      <Button
                        flex={1}
                        size="sm"
                        variant="danger-text"
                        onClick={() => handleDelete(evento.EventoId!)}
                      >
                        🗑️ Elimina
                      </Button>
                    </HStack>
                    <Button
                      w="full"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleOpenNewsletterPreview(evento.EventoId!)}
                    >
                      📧 Invia Newsletter
                    </Button>
                  </VStack>
                </CardFooter>
              )}
            </Card>
              );
            })}
        </Grid>
      )}

      {/* Gift Cards Section (Admin only) */}
      {isAuthenticated && (
        <Box mt={12}>
          <Heading size="lg" mb={6}>
            Gift Cards
          </Heading>
          
          {loadingGiftCards ? (
            <Flex justify="center" py={6}>
              <Spinner size="lg" color="gray.400" />
            </Flex>
          ) : giftCards.length === 0 ? (
            <Card bg="gray.850" borderColor="gray.700">
              <CardBody textAlign="center" py={8}>
                <Text color="gray.400" mb={4}>Nessuna gift card creata.</Text>
                <Button variant="primary" onClick={() => setShowGiftCardModal(true)}>
                  Crea la prima Gift Card
                </Button>
              </CardBody>
            </Card>
          ) : (
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
              {giftCards.map((giftCard) => {
                const isPending = !giftCard.Approvata && !giftCard.Utilizzata;
                const isUsed = giftCard.Utilizzata;
                
                return (
                <Card
                  key={giftCard.GiftCardId}
                  bg="gray.850"
                  borderColor={isPending ? "yellow.600" : "gray.700"}
                  borderWidth="1px"
                  opacity={isUsed ? 0.6 : 1}
                >
                  <CardHeader pb={2}>
                    <Flex justify="space-between" align="center">
                      <Badge 
                        bg="gray.700" 
                        color="white"
                        fontSize="md" 
                        px={3} 
                        py={1}
                      >
                        {giftCard.Codice}
                      </Badge>
                      <Badge 
                        bg={isPending ? "yellow.600" : isUsed ? "gray.700" : "green.600"} 
                        color={isPending ? "yellow.100" : isUsed ? "gray.400" : "green.100"}
                      >
                        {isPending ? "In attesa" : isUsed ? "Utilizzata" : "Approvata"}
                      </Badge>
                    </Flex>
                  </CardHeader>
                  <CardBody py={3}>
                    <VStack align="stretch" spacing={2} fontSize="sm">
                      <HStack justify="space-between">
                        <Text color="gray.400">Intestatario</Text>
                        <Text>{giftCard.Nome} {giftCard.Cognome}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text color="gray.400">Partecipanti</Text>
                        <Text>{giftCard.NumeroPersone}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text color="gray.400">Bevande</Text>
                        <Text>🍷 {giftCard.NumeroAlcolico} | 🥤 {giftCard.NumeroAnalcolico}</Text>
                      </HStack>
                      {giftCard.DataCreazione && (
                        <HStack justify="space-between">
                          <Text color="gray.400">Creata</Text>
                          <Text>{new Date(giftCard.DataCreazione).toLocaleDateString('it-IT')}</Text>
                        </HStack>
                      )}
                    </VStack>
                  </CardBody>
                  <CardFooter pt={0}>
                    <HStack w="full" spacing={2}>
                      {isPending && (
                        <Button
                          flex={1}
                          size="sm"
                          bg="green.600"
                          color="white"
                          _hover={{ bg: "green.500" }}
                          onClick={() => handleApproveGiftCard(giftCard.GiftCardId!)}
                        >
                          ✓ Approva
                        </Button>
                      )}
                      <Button
                        flex={1}
                        size="sm"
                        variant="danger-text"
                        onClick={() => handleDeleteGiftCard(giftCard.GiftCardId!)}
                        isDisabled={isUsed}
                      >
                        🗑️ Elimina
                      </Button>
                    </HStack>
                  </CardFooter>
                </Card>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* Booking Modal */}
      {selectedEvento && (
        <BookingModal
          evento={selectedEvento}
          isWaitingList={isWaitingList}
          onClose={handleCloseModal}
          onSuccess={handleBookingSuccess}
          isAdminView={isAuthenticated}
        />
      )}

      {/* Gift Card Modal */}
      {showGiftCardModal && (
        <GiftCardModal
          onClose={() => {
            setShowGiftCardModal(false);
            navigate('/', { replace: true });
          }}
          onSuccess={handleGiftCardSuccess}
        />
      )}

      {/* Menu Modal */}
      <Modal isOpen={!!menuEvento} onClose={() => setMenuEvento(null)} size="lg" isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent bg="gray.800" borderColor="gray.700" borderWidth="1px">
          <ModalHeader borderBottom="1px" borderColor="gray.700">
            <HStack spacing={2}>
              <Text>📜</Text>
              <Text>Menu - {menuEvento?.Titolo}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {menuEvento?.Menu ? (
              <Box
                dangerouslySetInnerHTML={{ __html: menuEvento.Menu }}
                sx={{
                  "& p": { margin: "0 0 0.5em 0" },
                  "& p:last-child": { marginBottom: 0 },
                  "& ul, & ol": { paddingLeft: "1.5em", margin: "0.5em 0" },
                  "& li": { marginBottom: "0.25em" },
                  "& strong": { fontWeight: "bold" },
                  "& em": { fontStyle: "italic" },
                }}
              />
            ) : (
              <Text color="gray.500" fontStyle="italic">
                Nessun menu disponibile.
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Newsletter Preview Modal */}
      <Modal isOpen={newsletterEventoId !== null} onClose={handleCloseNewsletterPreview} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent bg="gray.800" borderColor="gray.700" borderWidth="1px">
          <ModalHeader borderBottom="1px" borderColor="gray.700">
            <HStack spacing={2}>
              <Text>📧</Text>
              <Text>Anteprima Newsletter</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {loadingNewsletterPreview ? (
              <Flex justify="center" py={12}>
                <Spinner size="xl" color="gray.400" />
              </Flex>
            ) : newsletterHtml ? (
              <Box borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.700">
                <iframe
                  title="Anteprima Newsletter"
                  srcDoc={newsletterHtml}
                  style={{ width: "100%", height: "70vh", border: "none", backgroundColor: "#171923" }}
                />
              </Box>
            ) : (
              <Text color="gray.500" fontStyle="italic">
                Impossibile caricare l'anteprima.
              </Text>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor="gray.700" gap={3}>
            <Text fontSize="xs" color="gray.500" flex={1}>
              Verrà inviata solo a scarpetta.homerestaurant@gmail.com
            </Text>
            <Button variant="ghost" onClick={handleCloseNewsletterPreview} isDisabled={sendingNewsletter}>
              Annulla
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSendNewsletter}
              isLoading={sendingNewsletter}
              isDisabled={loadingNewsletterPreview || !newsletterHtml}
            >
              Invia
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Home;
